import React from 'react';

export interface AboutHeroData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
}

interface AboutHeroProps {
  data: AboutHeroData;
  isEditing?: boolean;
  onChange?: (data: AboutHeroData) => void;
}

const AboutHero: React.FC<AboutHeroProps> = ({ data, isEditing = false, onChange }) => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-[#050008] pt-20">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d0020] via-[#050008] to-[#000d1a]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(140,82,255,0.2)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(0,194,255,0.1)_0%,transparent_60%)]" />
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(140,82,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(140,82,255,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center py-20 w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/5 border border-[#8c52ff]/30 rounded-full px-4 py-2 mb-6">
          <i className="ri-user-heart-line text-[#8c52ff] text-sm" />
          {isEditing ? (
            <input
              type="text"
              value={data.badge}
              onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
              className="bg-purple-950/40 border border-purple-500/50 rounded px-2 py-0.5 text-[#8c52ff] text-xs font-bold uppercase tracking-widest focus:outline-none"
              placeholder="Badge"
            />
          ) : (
            <span className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest">{data.badge}</span>
          )}
        </div>

        {/* Title */}
        {isEditing ? (
          <div className="space-y-3 mb-6 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 text-left">
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Prefijo de Título</label>
                <input
                  type="text"
                  value={data.titlePrefix}
                  onChange={(e) => onChange && onChange({ ...data, titlePrefix: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-white font-bold text-lg focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="flex-1 text-left">
                <label className="text-[11px] text-cyan-400 uppercase tracking-wider block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={data.titleHighlight}
                  onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                  className="w-full bg-black/40 border border-cyan-500/40 rounded-lg px-3 py-2 text-cyan-300 font-bold text-lg focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Subtítulo Descriptivo</label>
              <textarea
                value={data.subtitle}
                onChange={(e) => onChange && onChange({ ...data, subtitle: e.target.value })}
                rows={3}
                className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-white/80 text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {data.titlePrefix}
              <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                {data.titleHighlight}
              </span>
            </h1>
            <p className="text-white/60 text-base md:text-xl leading-relaxed max-w-2xl mx-auto">
              {data.subtitle}
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default AboutHero;
